import { Autocomplete, AutocompleteProps, useJsApiLoader } from '@react-google-maps/api';
import { ChangeEvent, useState } from 'react';
import { styled } from '@mui/material';

import Text from '../Text';
import { InputBackgroundType, backgroundColors } from './index';
import { STROKE } from '../../Helpers/colors';

const googleMapsApiKey = process.env.REACT_APP_GOOGLE_PLACES_KEY ?? '';

interface AutocompletePropperties extends AutocompleteProps {
	backgroundType?: InputBackgroundType;
}

interface AddressDetails {
	city?: string;
	fullAddress?: string;
	zipcode?: string;
}
interface AddressInputProperties {
	handleInputBlur?: () => void;
	setAddressDetails: ({ fullAddress, city, zipcode }: AddressDetails) => void;
	value: string;
}

const AddressInputStyled = styled(Autocomplete)<AutocompletePropperties>(({ backgroundType = 'primary' }) => ({
	width: '100%',
	height: '40px',
	padding: '0 20px',
	backgroundColor: backgroundColors[backgroundType],
	border: `1px solid ${STROKE.SECONDARY}`,
	borderRadius: '9px !important',
	textTransform: 'none',
	display: 'inline-flex !important',
	fontFamily: 'Inter',
	marginBottom: '16px',
	input: {
		width: '100%',
		border: 'none',
		fontFamily: 'Inter',
		margin: 0,
		padding: '6px 0 7px',
		background: 'none',
		animationName: 'mui-auto-fill-cancel',
		letterSpacing: 'inherit',
		animationDuration: '10ms',
		// -webkitTapHighlightColor: "transparent",
		height: '40px',
		'&:focus-visible': {
			outline: 'none',
		},
		' &:: placeholder': {
			color: STROKE.SECONDARY,
		},
	},
}));

const AddressInput = ({ setAddressDetails, value, handleInputBlur }: AddressInputProperties) => {
	const [autocompleteValue, setAutocompleteValue] = useState<google.maps.places.Autocomplete | null>(null);

	const { isLoaded } = useJsApiLoader({
		id: 'google-map-script',
		googleMapsApiKey,
		libraries: ['places'],
	});

	const placeChangeHandler = () => {
		if (!autocompleteValue) return;

		// eslint-disable-next-line camelcase
		const { address_components, formatted_address } = autocompleteValue.getPlace();

		// eslint-disable-next-line camelcase
		const zipcode = address_components?.find((component) => component.types[0] === 'postal_code')?.long_name;

		// eslint-disable-next-line camelcase
		const city = address_components?.find((component) => component.types[0] === 'locality')?.long_name;

		// eslint-disable-next-line camelcase
		setAddressDetails({ fullAddress: formatted_address, city, zipcode });
	};

	const handleInputChange = (event: ChangeEvent<HTMLInputElement>) => {
		const { value: inputValue } = event.target;

		if (!inputValue) {
			setAddressDetails({ fullAddress: '' });

			return;
		}
		setAddressDetails({ fullAddress: inputValue });
	};

	return isLoaded ? (
		<>
			<Text title="Address" className="inputLabel active" type="psm" fontWeight="medium" />
			<AddressInputStyled
				onLoad={(autocomplete) => setAutocompleteValue(autocomplete)}
				onPlaceChanged={placeChangeHandler}
			>
				<input id="address-line" placeholder="" onChange={handleInputChange} value={value} onBlur={handleInputBlur} />
			</AddressInputStyled>
		</>
	) : null;
};

export default AddressInput;
